import React, { useState, useEffect, useCallback } from 'react';
import {
    X, CreditCard, Loader2, CheckCircle2, Copy, Clock,
    Smartphone, QrCode, Building2, AlertCircle, ChevronRight,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Payment method categories
const PAYMENT_CATEGORIES = [
    {
        label: 'QRIS',
        icon: QrCode,
        banks: [{ code: 7, name: 'QRIS Dynamic', shortName: 'QRIS' }],
    },
    {
        label: 'E-Wallet',
        icon: Smartphone,
        banks: [
            { code: 1, name: 'Dana', shortName: 'Dana' },
            { code: 2, name: 'Gopay', shortName: 'Gopay' },
            { code: 3, name: 'OVO', shortName: 'OVO' },
            { code: 4, name: 'ShopeePay', shortName: 'ShopeePay' },
            { code: 5, name: 'LinkAja', shortName: 'LinkAja' },
            { code: 6, name: 'Ottocash', shortName: 'Ottocash' },
        ],
    },
    {
        label: 'Virtual Account',
        icon: Building2,
        banks: [
            { code: 9, name: 'Bank Central Asia (BCA)', shortName: 'BCA' },
            { code: 14, name: 'Bank Negara Indonesia (BNI)', shortName: 'BNI' },
            { code: 13, name: 'Bank Rakyat Indonesia (BRI)', shortName: 'BRI' },
            { code: 10, name: 'Bank Mandiri', shortName: 'Mandiri' },
            { code: 8, name: 'Bank CIMB Niaga', shortName: 'CIMB' },
            { code: 15, name: 'Bank Permata', shortName: 'Permata' },
            { code: 16, name: 'Bank Danamon', shortName: 'Danamon' },
            { code: 12, name: 'Bank Panin', shortName: 'Panin' },
            { code: 11, name: 'Bank INA (BINA)', shortName: 'BINA' },
        ],
    },
];

const formatPrice = (price) => new Intl.NumberFormat('id-ID').format(price);

const VoucherPurchaseModal = ({ isOpen, onClose, voucher }) => {
    const [step, setStep] = useState(1); // 1=form, 2=confirm, 3=loading, 4=result
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        bankCode: null,
    });
    const [selectedBank, setSelectedBank] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState('pending');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setFormData({ firstName: '', lastName: '', email: '', phoneNumber: '', bankCode: null });
            setSelectedBank(null);
            setPaymentResult(null);
            setPaymentStatus('pending');
            setError(null);
        }
    }, [isOpen]);

    // Poll payment status when on result step (skip for QRIS - no check-status)
    useEffect(() => {
        const isQris = formData.bankCode === 7;
        if (!isOpen || step !== 4 || !paymentResult?.idtrx || paymentStatus === 'paid' || isQris) return;

        const interval = setInterval(async () => {
            try {
                const res = await axios.get(`${API}/voucher/status/${paymentResult.idtrx}`);
                if (res.data?.status === 'paid') {
                    setPaymentStatus('paid');
                    toast.success('Pembayaran berhasil! 🎉');
                }
            } catch (err) {
                // Silent retry
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [isOpen, step, paymentResult, paymentStatus, formData.bankCode]);

    const handleSubmitForm = (e) => {
        e.preventDefault();
        if (!formData.firstName || !formData.email || !formData.phoneNumber || !formData.bankCode) {
            toast.error('Mohon lengkapi semua data');
            return;
        }
        setStep(2);
    };

    const handleConfirm = async () => {
        setStep(3);
        setLoading(true);
        setError(null);

        try {
            const form = new FormData();
            form.append('voucher_id', voucher.id);
            form.append('first_name', formData.firstName);
            form.append('last_name', formData.lastName);
            form.append('email', formData.email);
            form.append('phone_number', formData.phoneNumber);
            form.append('bank_code', formData.bankCode);

            const res = await axios.post(`${API}/voucher/purchase`, form);

            if (res.data?.success) {
                setPaymentResult(res.data);
                setStep(4);
            } else {
                throw new Error(res.data?.detail || 'Gagal membuat pembayaran');
            }
        } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Terjadi kesalahan');
            setStep(2); // Go back to confirm
            toast.error('Gagal membuat pembayaran. Silakan coba lagi.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = useCallback((text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Disalin!');
        setTimeout(() => setCopied(false), 2000);
    }, []);

    if (!isOpen || !voucher) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
                data-testid="voucher-purchase-modal">

                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-[#0a0a1a] to-[#1a1a2e] text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
                    <div>
                        <h3 className="font-bold text-lg">Beli Gym Voucher</h3>
                        <p className="text-white/60 text-sm">Rp{formatPrice(voucher.value)}</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-6">
                    {/* Step 1: Form */}
                    {step === 1 && (
                        <form onSubmit={handleSubmitForm} className="space-y-4">
                            <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-500">Nilai Voucher</span>
                                    <span className="font-bold text-lg text-[#0052CC]">Rp{formatPrice(voucher.value)}</span>
                                </div>
                                <div className="flex justify-between items-center mt-1">
                                    <span className="text-sm text-gray-500">Harga</span>
                                    <span className="font-bold text-[#AAFF00] bg-[#0a0a1a] rounded-lg px-2 py-0.5">
                                        Rp{formatPrice(voucher.buyPrice)}
                                    </span>
                                </div>
                            </div>

                            {/* Name */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nama Depan *</label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 outline-none text-sm"
                                        placeholder="John"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nama Belakang</label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 outline-none text-sm"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 outline-none text-sm"
                                    placeholder="user@email.com"
                                    required
                                />
                            </div>

                            {/* Phone */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">No. HP *</label>
                                <input
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 outline-none text-sm"
                                    placeholder="08xxxxxxxxxx"
                                    required
                                />
                            </div>

                            {/* Payment Method */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Metode Pembayaran *</label>
                                <div className="space-y-3">
                                    {PAYMENT_CATEGORIES.map((category) => (
                                        <div key={category.label}>
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <category.icon className="w-4 h-4 text-gray-400" />
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{category.label}</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2">
                                                {category.banks.map((bank) => (
                                                    <button
                                                        key={bank.code}
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData({ ...formData, bankCode: bank.code });
                                                            setSelectedBank(bank);
                                                        }}
                                                        className={`px-3 py-2 rounded-xl border text-xs font-medium transition-all ${formData.bankCode === bank.code
                                                            ? 'border-[#0052CC] bg-[#0052CC]/5 text-[#0052CC] ring-2 ring-[#0052CC]/20'
                                                            : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {bank.shortName}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-[#FF9900] hover:bg-[#E68A00] text-white rounded-xl py-3 font-bold text-sm mt-4 flex items-center justify-center gap-2"
                                disabled={!formData.bankCode}
                            >
                                Lanjutkan
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </form>
                    )}

                    {/* Step 2: Confirmation */}
                    {step === 2 && (
                        <div className="space-y-4">
                            <h4 className="font-bold text-gray-900">Konfirmasi Pembelian</h4>

                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}

                            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-gray-500">Voucher</span><span className="font-medium">{voucher.label || `Rp${formatPrice(voucher.value)}`}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Nama</span><span className="font-medium">{formData.firstName} {formData.lastName}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium">{formData.email}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">No. HP</span><span className="font-medium">{formData.phoneNumber}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Bayar Via</span><span className="font-medium">{selectedBank?.name}</span></div>
                                <hr className="border-gray-200" />
                                <div className="flex justify-between"><span className="text-gray-500 font-semibold">Total Bayar</span><span className="font-bold text-lg text-[#0052CC]">Rp{formatPrice(voucher.buyPrice)}</span></div>
                            </div>

                            <div className="flex gap-3">
                                <Button onClick={() => { setStep(1); setError(null); }} variant="outline" className="flex-1 rounded-xl py-3">
                                    Kembali
                                </Button>
                                <Button onClick={handleConfirm} className="flex-1 bg-[#FF9900] hover:bg-[#E68A00] text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
                                    <CreditCard className="w-4 h-4" />
                                    Bayar
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Loading */}
                    {step === 3 && (
                        <div className="text-center py-12">
                            <Loader2 className="w-12 h-12 animate-spin text-[#0052CC] mx-auto mb-4" />
                            <h4 className="font-bold text-gray-900 mb-1">Memproses Pembayaran...</h4>
                            <p className="text-sm text-gray-500">Mohon tunggu, sedang membuat virtual account</p>
                        </div>
                    )}

                    {/* Step 4: Payment Result */}
                    {step === 4 && paymentResult && (
                        <div className="space-y-4">
                            {paymentStatus === 'paid' ? (
                                // Success state
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle2 className="w-8 h-8 text-green-500" />
                                    </div>
                                    <h4 className="font-bold text-xl text-gray-900 mb-1">Pembayaran Berhasil! 🎉</h4>
                                    <p className="text-sm text-gray-500 mb-4">Voucher gym kamu telah aktif</p>
                                    <div className="bg-green-50 rounded-xl p-4">
                                        <p className="text-sm text-green-700 font-medium">
                                            {paymentResult.voucher?.label}
                                        </p>
                                        <p className="text-2xl font-bold text-green-700 mt-1">
                                            Rp{formatPrice(paymentResult.voucher?.value)}
                                        </p>
                                    </div>
                                    <Button onClick={onClose} className="w-full mt-6 bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-xl py-3 font-bold">
                                        Selesai
                                    </Button>
                                </div>
                            ) : (
                                // Pending - show VA/QR
                                <>
                                    <div className="text-center">
                                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <Clock className="w-6 h-6 text-amber-500" />
                                        </div>
                                        <h4 className="font-bold text-gray-900">Menunggu Pembayaran</h4>
                                        <p className="text-xs text-gray-500 mt-1">Selesaikan pembayaran sebelum batas waktu</p>
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Metode</span>
                                            <span className="font-medium">{paymentResult.payment?.bank_name}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Total</span>
                                            <span className="font-bold text-[#0052CC]">Rp{formatPrice(paymentResult.payment?.amount)}</span>
                                        </div>

                                        {/* VA Number */}
                                        {paymentResult.payment?.va_number && (
                                            <div className="bg-white rounded-xl p-4 border border-gray-200">
                                                <p className="text-xs text-gray-500 mb-1">Nomor Virtual Account</p>
                                                <div className="flex items-center justify-between">
                                                    <span className="font-mono text-lg font-bold text-gray-900 tracking-wider">
                                                        {paymentResult.payment.va_number}
                                                    </span>
                                                    <button
                                                        onClick={() => copyToClipboard(paymentResult.payment.va_number)}
                                                        className="text-[#0052CC] hover:bg-[#0052CC]/10 p-2 rounded-lg transition-colors"
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* E-wallet payment link */}
                                        {paymentResult.payment?.redirect_url && (
                                            <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                                                <p className="text-xs text-gray-500 mb-3">Klik tombol di bawah untuk membayar</p>
                                                <a
                                                    href={paymentResult.payment.redirect_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center gap-2 bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-xl px-6 py-3 font-bold text-sm transition-all"
                                                >
                                                    <Smartphone className="w-4 h-4" />
                                                    Bayar dengan {paymentResult.payment?.bank_name}
                                                </a>
                                                <p className="text-xs text-gray-400 mt-2">Anda akan diarahkan ke halaman pembayaran e-wallet</p>
                                            </div>
                                        )}

                                        {/* Fallback: show trxRef when no payment info at all */}
                                        {!paymentResult.payment?.va_number && !paymentResult.payment?.qr_data && !paymentResult.payment?.redirect_url && (
                                            <div className="bg-white rounded-xl p-4 border border-amber-200">
                                                <p className="text-xs text-gray-500 mb-1">Referensi Transaksi</p>
                                                <div className="flex items-center justify-between">
                                                    <span className="font-mono text-sm font-bold text-gray-900 tracking-wider">
                                                        {paymentResult.idtrx}
                                                    </span>
                                                    <button
                                                        onClick={() => copyToClipboard(paymentResult.idtrx)}
                                                        className="text-[#0052CC] hover:bg-[#0052CC]/10 p-2 rounded-lg transition-colors"
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <p className="text-[10px] text-amber-600 mt-2 bg-amber-50 rounded-lg px-2 py-1">
                                                    ⚠️ Nomor VA belum tersedia. Silakan hubungi admin.
                                                </p>
                                            </div>
                                        )}

                                        {/* QR Code for QRIS */}
                                        {paymentResult.payment?.qr_data && (
                                            <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                                                <p className="text-xs text-gray-500 mb-3">Scan QR Code</p>
                                                <div className="bg-white p-4 rounded-lg inline-block border">
                                                    <img
                                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(paymentResult.payment.qr_data)}`}
                                                        alt="QRIS QR Code"
                                                        className="w-48 h-48"
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-400 mt-2">Gunakan aplikasi e-wallet untuk scan</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="bg-blue-50 rounded-xl p-3 flex gap-2">
                                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-blue-600">Status akan diperbarui otomatis setelah pembayaran...</p>
                                    </div>

                                    <Button onClick={onClose} variant="outline" className="w-full rounded-xl py-3">
                                        Tutup
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoucherPurchaseModal;
